import { useState, useEffect, useCallback } from 'react'
import { Link, useParams, useNavigate } from 'react-router-dom'
import JSZip from 'jszip'
import Navbar from '../components/Navbar'
import PageHeader from '../components/PageHeader'
import { usePractice } from '../hooks/usePractice'
import {
  getRequestById, getAttachments, deleteAttachmentAndRevert,
  deleteTransaction, deleteRequest, updateRequestNotes,
  cleanupRequestFiles, getSettings, sendClientRequestEmail,
  saveBookkeeperNote, updateRequestReminderSettings,
  addTransactionsToRequest, updateTransactionDescriptions,
  cancelRequest, reopenRequest, getReceiptSignedUrl,
} from '../lib/supabase'
import { parseCSV, parseRawCsv } from '../lib/csvParser'
import CsvColumnMapper from '../components/CsvColumnMapper'
import { useToast } from '../components/Toast'
import usePageTitle from '../hooks/usePageTitle'
import { formatDate, formatCurrency } from '../lib/csvParser'

// ─── Receipt filename builder ─────────────────────────────────────────────────
// Builds a human-friendly filename from a transaction's date/vendor/amount.
// Format: YYYY-MM-DD_Vendor_£Amount.ext
// Sorts chronologically when listed alphabetically. Vendor is best-effort —
// if extraction fails, falls back to a cleaned version of the description.

// Strip "INT'L 0123456789 " card-transaction prefixes
const INTL_PREFIX_RE = /^INT['']?L\s+\d+\s+/i

// Strip "Paid to / Sent (money/payment/etc) to / Received from / Transfer to/from /
// Card transaction of / Bill payment to / Faster payment to / Direct debit to"
// prefixes. These are bank-narration boilerplate, not vendor names.
const NARRATION_PREFIX_RE = /^(paid (to|from)|sent (money |payment |funds )?(to|from)|received (money |payment |funds )?(to|from)|transfer (to|from)|bill payment.*?(to|from)|faster payment.*?(to|from)|direct debit.*?(to|from))\s+/i

// Xero "Card transaction of [amount] [CCY] issued by [VENDOR] +[phone]"
// Pull the vendor name out directly. Capture group 1 = vendor.
const XERO_CARD_RE = /card transaction of\s+[\d.,]+\s+[A-Z]{3}\s+issued by\s+(.+?)(?:\s+\+\d|\s+cardholder|$)/i

// Strip everything from " USD/EUR/GBP NN.NN @ N.NNNN" onwards (Sage-style FX detail).
// This must NOT match Xero's "(GBP 889 at 0.74 rate)" since that's covered by the parens-strip.
const FX_DETAIL_RE = /\s+(USD|EUR|GBP|JPY|AUD|CAD|CHF|CNY)\s+[\d.,]+\s+@\s+[\d.,]+.*$/i

// Strip trailing legal-entity suffixes
const SUFFIX_RE = /\s+(LTD|LLC|INC|PLC|CORP|CO|GMBH|SA|BV|PTE|LLP)\.?$/i

// Common merchant aliases — first match wins. Order matters.
// Each entry: [pattern (case-insensitive substring or regex), display name]
const VENDOR_ALIASES = [
  [/\bopenai\b|\bchatgpt\b/i,         'OpenAI'],
  [/\banthropic\b|\bclaude\.ai\b/i,    'Anthropic'],
  [/\bmistral\.?ai\b/i,                'Mistral'],
  [/\bgithub\b/i,                      'GitHub'],
  [/\bgoogle\b|\bgcp\b/i,              'Google'],
  [/\bmicrosoft\b|\bazure\b/i,         'Microsoft'],
  [/\bauth0\b/i,                       'Auth0'],
  [/mongodb/i,                         'MongoDB'],
  [/\baws\b|\bamazon web/i,            'AWS'],
  [/\bamazon\b/i,                      'Amazon'],
  [/\bgodaddy\b/i,                     'GoDaddy'],
  [/\bxero\b/i,                        'Xero'],
  [/\bquickbooks\b|\bintuit\b/i,       'QuickBooks'],
  [/\bsage\b/i,                        'Sage'],
  [/\bdropbox\b/i,                     'Dropbox'],
  [/\bnotion\b/i,                      'Notion'],
  [/\bslack\b/i,                       'Slack'],
  [/\bzoom\b/i,                        'Zoom'],
  [/\bstripe\b/i,                      'Stripe'],
  [/\bpaypal\b/i,                      'PayPal'],
  [/\bhsbc\b/i,                        'HSBC'],
  [/\bbarclays\b/i,                    'Barclays'],
  [/\blloyds\b/i,                      'Lloyds'],
  [/\bnatwest\b/i,                     'NatWest'],
  [/\bsantander\b/i,                   'Santander'],
  [/\bnationwide\b/i,                  'Nationwide'],
  [/\btesco\b/i,                       'Tesco'],
  [/\bsainsbury/i,                     'Sainsburys'],
  [/\basda\b/i,                        'Asda'],
  [/\bmorrisons\b/i,                   'Morrisons'],
  [/\bwaitrose\b/i,                    'Waitrose'],
  [/\bvodafone\b/i,                    'Vodafone'],
  [/\bee\b/i,                          'EE'],
  [/\bo2\b/i,                          'O2'],
  [/\bbt\b/i,                          'BT'],
]

// Try to pull a sensible vendor name from a free-text bank description.
function extractVendorName(description) {
  if (!description) return ''
  let s = description.trim()

  // Pass 0: Xero "Card transaction of X CCY issued by VENDOR +phone"
  // Try this first because the FX info appears BEFORE the vendor in this format,
  // which would confuse the rest of the cleanup.
  const xeroCardMatch = s.match(XERO_CARD_RE)
  if (xeroCardMatch) {
    s = xeroCardMatch[1].trim()
    // Then fall through to alias check + title-casing
  }

  // Pass 1: known vendor aliases — highest confidence
  for (const [pattern, name] of VENDOR_ALIASES) {
    if (pattern.test(s)) return name
  }

  // Pass 2: clean up structural noise
  s = s.replace(INTL_PREFIX_RE, '')          // INT'L 0123456789
  s = s.replace(/\([^)]*\)/g, ' ')           // Strip parenthesised content first
  s = s.replace(FX_DETAIL_RE, '')            // USD 24.00 @ 1.3385 (Sage-style only now)
  s = s.replace(NARRATION_PREFIX_RE, '')     // Paid to / Sent money to / Card transaction of
  s = s.replace(/\s+\*\s+/g, ' ')            // OPENAI * CHATGPT → OPENAI CHATGPT
  s = s.replace(/\*+/g, ' ')                 // DNH*GODADDY → DNH GODADDY
  s = s.replace(/[#@=]/g, ' ')               // GODADDY#401 / =5000.00 → spaces
  s = s.replace(/\b\d{6,}\b/g, '')           // long ref numbers
  s = s.replace(/\s+-\s+/g, ' ')             // " - " separator (TOWER HAMLETS - C.TAX)
  s = s.replace(/\s+/g, ' ').trim()

  // Pass 3: re-check aliases on the cleaned string
  for (const [pattern, name] of VENDOR_ALIASES) {
    if (pattern.test(s)) return name
  }

  // Pass 4: title-case fallback. Strip legal-entity suffix, strip
  // reference-code-looking tokens and single-letter initials, take first 3 words.
  s = s.replace(SUFFIX_RE, '')
  const words = s
    .split(/\s+/)
    .filter(w => w.length > 0)
    // Drop tokens with digits in them (transaction refs)
    .filter(w => !/\d/.test(w))
    // Drop FX/timestamp leftovers
    .filter(w => !/^(FXTimeStamp|midmarketrate|TargetAmount)/i.test(w))
    // Drop single letters (initials like "L B" in "L B TOWER HAMLETS")
    .filter(w => w.length > 1 || /^[a-z]$/i.test(w) === false)
    .slice(0, 3)

  return words
    .map(w => {
      // Keep all-caps acronyms (2-4 letters), title-case everything else
      if (/^[A-Z]{2,4}$/.test(w)) return w
      return w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()
    })
    .join(' ')
    .trim()
}

// Sanitise a string for use in a filename — strips chars that filesystems reject
function sanitiseForFilename(s) {
  return s
    .replace(/[<>:"/\\|?*\x00-\x1f]/g, '') // Windows + control chars
    .replace(/\s+/g, ' ')
    .trim()
}

// Build a renamed filename from a transaction and the original file's extension
function buildReceiptFilename(transaction, originalFilename) {
  // Extract the original extension (lowercased), defaulting to .pdf if none
  const extMatch = originalFilename.match(/\.([a-z0-9]{1,8})$/i)
  const ext = extMatch ? `.${extMatch[1].toLowerCase()}` : ''

  // Date in ISO format for chronological sorting
  let datePart = ''
  if (transaction.date) {
    const d = new Date(transaction.date)
    if (!isNaN(d)) {
      const yyyy = d.getFullYear()
      const mm = String(d.getMonth() + 1).padStart(2, '0')
      const dd = String(d.getDate()).padStart(2, '0')
      datePart = `${yyyy}-${mm}-${dd}`
    }
  }

  // Vendor — prefer the dedicated payee field if the source provided one
  // (Xero/QuickBooks). Fall back to extracting from the description (Sage etc).
  // Final fallback: "Receipt" so we always have something.
  const payeeRaw = (transaction.payee || '').trim()
  const vendorSource = payeeRaw || extractVendorName(transaction.description)
  const vendor = sanitiseForFilename(vendorSource) || 'Receipt'

  // Amount — always positive, two decimals, with £ sign
  const amount = Math.abs(transaction.amount || 0)
  const amountPart = `£${amount.toFixed(2)}`

  const parts = [datePart, vendor, amountPart].filter(Boolean)
  return `${parts.join('_')}${ext}`
}

// ─── CSV export helpers ──────────────────────────────────────────────────────

// Quote a single value for CSV: wrap in double-quotes, escape internal quotes
function csvCell(val) {
  if (val === null || val === undefined) return ''
  const s = String(val)
  // Always quote — safest against commas, newlines, and leading-zero loss in Excel
  return '"' + s.replace(/"/g, '""') + '"'
}

// Build a CSV string summarising every transaction in the request, with the
// renamed receipt filenames so the bookkeeper can cross-reference against
// the zipped attachments.
function buildSummaryCSV(transactions, attachmentsByTxId) {
  const headers = [
    'Date',
    'Description',
    'Type',
    'Amount',
    'Status',
    'Receipt filenames',
    'Lost explanation',
    'Wholly business',
    'Client note',
    'Bookkeeper note',
  ]

  const rows = transactions.map(tx => {
    const attachments = attachmentsByTxId[tx.id] || []
    const renamedFiles = attachments
      .map(a => buildReceiptFilename(tx, a.file_name))
      .join('; ')

    const type = (tx.amount || 0) > 0 ? 'Income' : 'Expenditure'
    const amount = Math.abs(tx.amount || 0).toFixed(2)
    const wholly = tx.lost_wholly_business === null || tx.lost_wholly_business === undefined
      ? ''
      : (tx.lost_wholly_business ? 'Yes' : 'No')

    return [
      tx.date || '',
      tx.description || '',
      type,
      amount,
      tx.status || '',
      renamedFiles,
      tx.status === 'lost' ? (tx.lost_explanation || '') : '',
      wholly,
      tx.client_note || '',
      tx.bookkeeper_note || '',
    ].map(csvCell).join(',')
  })

  return [headers.map(csvCell).join(','), ...rows].join('\r\n')
}

// ─── Sortable column header ───────────────────────────────────────────────────

function SortTh({ label, field, sort, onSort, style = {} }) {
  const active = sort.field === field
  const next = active && sort.dir === 'asc' ? 'desc' : 'asc'
  return (
    <th
      onClick={() => onSort(field, next)}
      style={{
        cursor: 'pointer', userSelect: 'none',
        whiteSpace: 'nowrap', ...style,
      }}
    >
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        {label}
        <span style={{
          fontSize: '0.7rem', opacity: active ? 0.9 : 0.25,
          color: active ? 'var(--accent)' : 'inherit',
          transition: 'opacity 0.15s',
          lineHeight: 1,
        }}>
          {active ? (sort.dir === 'asc' ? '▲' : '▼') : '⇅'}
        </span>
      </span>
    </th>
  )
}

function sortRows(rows, sort) {
  return [...rows].sort((a, b) => {
    let av, bv
    switch (sort.field) {
      case 'date':        av = a.date || ''; bv = b.date || ''; break
      case 'description': av = (a.description || '').toLowerCase(); bv = (b.description || '').toLowerCase(); break
      case 'type':        av = a.amount > 0 ? 0 : 1; bv = b.amount > 0 ? 0 : 1; break
      case 'amount':      av = Math.abs(a.amount || 0); bv = Math.abs(b.amount || 0); break
      case 'status':      av = a.status; bv = b.status; break
      default:            av = a.sort_order; bv = b.sort_order
    }
    if (av < bv) return sort.dir === 'asc' ? -1 : 1
    if (av > bv) return sort.dir === 'asc' ?  1 : -1
    return 0
  })
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function CopyLinkBtn({ token }) {
  const [copied, setCopied] = useState(false)
  const url = `${window.location.origin}/request/${token}`
  const copy = async () => {
    await navigator.clipboard.writeText(url)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }
  return (
    <button className="btn btn-secondary" onClick={copy}>
      {copied ? '✓ Copied!' : '🔗 Copy client link'}
    </button>
  )
}

function blankManualRow() {
  // Default to expenditure because that's what bookkeepers are chasing
  // receipts for most of the time. The user can flip to income per row.
  // We store this as a string here; on save it gets converted to a signed
  // amount before being passed to the database (expenditure = negative).
  return { date: '', description: '', amount: '', payee: '', type: 'expenditure' }
}

function AmountCell({ amount }) {
  if (amount === null || amount === undefined) return <span style={{ color: 'var(--ink-faint)' }}>—</span>
  const isIncome = amount > 0
  return (
    <span style={{ fontWeight: 600, color: isIncome ? 'var(--green)' : 'var(--ink)' }}>
      {isIncome ? '+' : ''}{formatCurrency(amount)}
    </span>
  )
}

// ─── RequestReminderCard ──────────────────────────────────────────────────────
//
// Card on the request page showing whether an auto-reminder is scheduled for
// this request, and when. Read-only by default; clicking "Edit" reveals the
// override controls.
//
// Three read-only display variants:
//   1. reminder_sent_at is set      -> "Reminder sent on DD/MM/YYYY"
//   2. reminder_enabled is false    -> "Off for this request"
//   3. otherwise                    -> "Scheduled for DD/MM/YYYY (N days from send)"
//
// The card is also where the user later (Phase 2) will see "send now" and
// "skip reason" info. For now it's just status + edit.

function RequestReminderCard({
  request, settings,
  editing, onStartEdit, onCancel, onSave, saving,
  reminderOnDraft, setReminderOnDraft,
  reminderCustomDraft, setReminderCustomDraft,
  reminderDaysDraft, setReminderDaysDraft,
  // When true, this card is being rendered inside a flex row alongside
  // another card. We drop the marginBottom (parent supplies spacing) and
  // the fade-up-2 animation class (parent supplies that too) so the cards
  // line up cleanly without doubled margins.
  inlineCard,
}) {
  if (!request) return null

  // Resolve the effective schedule: per-request override wins, otherwise
  // fall back to the practice default (which itself defaults to 7 if not
  // configured yet).
  const practiceDefault = settings?.reminder_days_after_send ?? 7
  const effectiveDays   = request.reminder_days_after_send ?? practiceDefault
  const isOnForRequest  = request.reminder_enabled !== false
  const isOnForClient   = request.clients?.reminders_enabled !== false
  const isOnForPractice = settings?.reminders_enabled !== false
  const hasFired        = !!request.reminder_sent_at
  const usingCustom     = typeof request.reminder_days_after_send === 'number'

  // Compute the scheduled date for display: created_at + effectiveDays
  let scheduledDate = null
  if (request.created_at) {
    const d = new Date(request.created_at)
    d.setDate(d.getDate() + effectiveDays)
    scheduledDate = d
  }

  // If a reminder has fired, work out how many days after the request it
  // happened. This is more useful than just the date alone, since "sent 8
  // days after" tells the bookkeeper at a glance whether the schedule
  // worked or there was a delay.
  let daysAfterSend = null
  if (hasFired && request.created_at) {
    const ms = new Date(request.reminder_sent_at).getTime() - new Date(request.created_at).getTime()
    daysAfterSend = Math.round(ms / (24 * 60 * 60 * 1000))
  }

  const formatShortDate = (d) => {
    if (!d) return ''
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
  }

  return (
    <div
      className={inlineCard ? 'card' : 'card fade-up-2'}
      style={{ padding: '18px 24px', marginBottom: inlineCard ? 0 : 16 }}
    >
      <div className="flex-between" style={{ marginBottom: editing ? 14 : 0, flexWrap: 'wrap', gap: 8 }}>
        <span style={{ fontWeight: 500, fontSize: '0.88rem' }}>⏰ Auto-reminder</span>
        {!editing && (
          <button className="btn btn-ghost btn-sm" onClick={onStartEdit}>
            Edit
          </button>
        )}
      </div>

      {!editing ? (
        // ─── Read-only summary ────────────────────────────────────────────
        // Display priority (top wins):
        //   1. Already fired   -> "Sent on DD/MM/YYYY, N days after the request"
        //   2. Practice off    -> explain the practice-wide setting wins
        //   3. Client off      -> explain the client-level opt-out wins
        //   4. Request off     -> per-request override
        //   5. Otherwise       -> "Scheduled for ..." with the effective schedule
        <div style={{ fontSize: '0.86rem', color: 'var(--ink-muted)', lineHeight: 1.55 }}>
          {hasFired ? (
            <span style={{ color: 'var(--ink)' }}>
              ✓ Sent on{' '}
              <strong>{formatShortDate(new Date(request.reminder_sent_at))}</strong>
              {daysAfterSend !== null && (
                <>, {daysAfterSend} day{daysAfterSend === 1 ? '' : 's'} after the request was sent</>
              )}.
            </span>
          ) : !isOnForPractice ? (
            <span>
              Off because auto-reminders are disabled practice-wide. Turn them
              on from <strong>Settings → Auto-reminders</strong>.
            </span>
          ) : !isOnForClient ? (
            <span>
              Off because reminders are disabled for this client. You can
              change that on the client's page.
            </span>
          ) : !isOnForRequest ? (
            <span>Off for this request. No reminder will be sent.</span>
          ) : (
            <>
              Scheduled for{' '}
              <strong style={{ color: 'var(--ink)' }}>{formatShortDate(scheduledDate)}</strong>
              {' '}({effectiveDays} day{effectiveDays === 1 ? '' : 's'} after the request was sent
              {usingCustom ? ', custom schedule for this request' : ''}).
              {' '}Will be skipped if the request is complete by then.
            </>
          )}
        </div>
      ) : (
        // ─── Edit form ────────────────────────────────────────────────────
        <div>
          {/* On/off toggle */}
          <div
            style={{
              display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer',
              marginBottom: 14,
            }}
            onClick={() => setReminderOnDraft(v => !v)}
          >
            <div style={{
              width: 36, height: 20, borderRadius: 100, flexShrink: 0,
              background: reminderOnDraft ? 'var(--green)' : 'var(--surface-3)',
              position: 'relative', transition: 'background 0.2s',
            }}>
              <div style={{
                width: 14, height: 14, borderRadius: '50%', background: 'white',
                position: 'absolute', top: 3,
                left: reminderOnDraft ? 19 : 3,
                transition: 'left 0.2s',
                boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
              }} />
            </div>
            <span style={{ fontSize: '0.88rem', color: 'var(--ink)' }}>
              Send a reminder for this request
            </span>
          </div>

          {reminderOnDraft && (
            <>
              <label
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                  cursor: 'pointer', fontSize: '0.84rem', color: 'var(--ink-muted)',
                  marginBottom: 8,
                }}
              >
                <input
                  type="checkbox"
                  checked={reminderCustomDraft}
                  onChange={e => setReminderCustomDraft(e.target.checked)}
                  style={{ accentColor: 'var(--accent)' }}
                />
                Use a different schedule for this request
                {' '}(otherwise: practice default of {practiceDefault} day{practiceDefault === 1 ? '' : 's'})
              </label>

              {reminderCustomDraft && (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  marginTop: 6, marginBottom: 4,
                }}>
                  <input
                    type="number"
                    min={1}
                    max={90}
                    value={reminderDaysDraft}
                    onChange={e => setReminderDaysDraft(e.target.value)}
                    className="input"
                    style={{ width: 90 }}
                  />
                  <span style={{ fontSize: '0.85rem', color: 'var(--ink-muted)' }}>
                    days after the request was sent
                  </span>
                </div>
              )}
            </>
          )}

          <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
            <button className="btn btn-primary btn-sm" onClick={onSave} disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button className="btn btn-ghost btn-sm" onClick={onCancel} disabled={saving}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

// Internal note for bookkeepers — visible to all members of the practice,
// hidden from the client. The editor is opened by clicking the 📝 button in
// the actions column (see EditNoteButton) — this component just renders the
// note (if any) or the editor (when active).
function BookkeeperNoteCell({ transaction, isEditing, onStartEdit, onStopEdit, onSaved }) {
  const [value, setValue] = useState(transaction.bookkeeper_note || '')
  const [saving, setSaving] = useState(false)
  const toast = useToast()

  // Sync local value with prop when not actively editing
  useEffect(() => {
    if (!isEditing) setValue(transaction.bookkeeper_note || '')
  }, [transaction.bookkeeper_note, isEditing])

  const handleSave = async () => {
    setSaving(true)
    const { error } = await saveBookkeeperNote(transaction.id, value.trim() || null)
    setSaving(false)
    if (error) {
      toast('Could not save note: ' + error.message, 'error')
      return
    }
    onStopEdit()
    onSaved()
  }

  const handleCancel = () => {
    setValue(transaction.bookkeeper_note || '')
    onStopEdit()
  }

  if (isEditing) {
    return (
      <div style={{ marginBottom: 4 }}>
        <textarea
          className="input"
          value={value}
          onChange={e => setValue(e.target.value)}
          rows={2}
          autoFocus
          placeholder="Internal note — visible to your team, hidden from client…"
          style={{ fontSize: '0.78rem', resize: 'vertical', minWidth: 200, padding: '6px 8px' }}
        />
        <div style={{ display: 'flex', gap: 5, marginTop: 4 }}>
          <button
            className="btn btn-primary btn-sm"
            onClick={handleSave}
            disabled={saving}
            style={{ fontSize: '0.72rem', padding: '3px 9px' }}
          >
            {saving ? '…' : 'Save'}
          </button>
          <button
            className="btn btn-ghost btn-sm"
            onClick={handleCancel}
            style={{ fontSize: '0.72rem', padding: '3px 9px' }}
          >
            Cancel
          </button>
        </div>
      </div>
    )
  }

  if (transaction.bookkeeper_note) {
    return (
      <div
        onClick={onStartEdit}
        style={{
          fontSize: '0.76rem', color: 'var(--ink-muted)',
          marginBottom: 2, cursor: 'pointer',
          padding: '2px 6px', borderRadius: 4,
          background: 'rgba(20, 121, 113, 0.08)',
          borderLeft: '2px solid rgba(20, 121, 113, 0.5)',
        }}
        title="Click to edit"
      >
        🗒 {transaction.bookkeeper_note}
      </div>
    )
  }

  // No note + not editing: render nothing — the 📝 button in the actions
  // column is the discovery affordance.
  return null
}

function AttachmentList({ transactionId, onDeleted }) {
  const [attachments, setAttachments] = useState([])
  const toast = useToast()

  const load = useCallback(async () => {
    const { data } = await getAttachments(transactionId)
    const list = data || []
    // Generate a fresh signed URL per attachment (1 hour). These are what
    // the <a href> below uses. Calling in parallel keeps the row render
    // snappy even with several files on one transaction.
    const signed = await Promise.all(
      list.map(async (a) => {
        const { signedUrl } = await getReceiptSignedUrl(a.file_path)
        return { ...a, signedUrl }
      })
    )
    setAttachments(signed)
  }, [transactionId])

  useEffect(() => { load() }, [load])

  const handleDelete = async (a) => {
    if (!window.confirm(`Remove "${a.file_name}"?`)) return
    const { error } = await deleteAttachmentAndRevert(a.id, a.file_path, transactionId)
    if (error) { toast(error.message, 'error'); return }
    toast('File removed', 'default')
    await load()
    onDeleted()
  }

  if (!attachments.length) return <span style={{ color: 'var(--ink-faint)', fontSize: '0.8rem' }}>—</span>

  return (
    <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginTop: 4 }}>
      {attachments.map(a => (
        <div key={a.id} style={{
          display: 'inline-flex', alignItems: 'center', gap: 5,
          padding: '3px 9px', borderRadius: 6,
          background: 'var(--green-light)', border: '1px solid rgba(34,160,107,0.2)',
          fontSize: '0.76rem',
        }}>
          <a href={a.signedUrl || '#'} target="_blank" rel="noopener noreferrer"
            onClick={e => { if (!a.signedUrl) e.preventDefault() }}
            style={{ color: 'var(--green)', fontWeight: 500, textDecoration: 'none' }}>
            📎 {a.file_name}
          </a>
          <button
            onClick={() => handleDelete(a)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-faint)', fontSize: '0.82rem', padding: 0, lineHeight: 1 }}
            title="Remove file"
          >✕</button>
        </div>
      ))}
    </div>
  )
}

// ─── Lost receipt print view ──────────────────────────────────────────────────

function openLostReceiptsPrintView(request) {
  const lostTxs = (request.transactions || []).filter(t => t.status === 'lost')
  if (!lostTxs.length) return

  const rows = lostTxs.map(tx => `
    <tr>
      <td>${formatDate(tx.date)}</td>
      <td>${tx.description || '—'}</td>
      <td style="text-align:right;white-space:nowrap">${formatCurrency(tx.amount)}</td>
      <td>${tx.lost_explanation || '—'}</td>
      <td style="text-align:center;font-weight:600;color:${tx.lost_wholly_business ? '#22a06b' : '#d97706'}">
        ${tx.lost_wholly_business ? 'Yes' : 'No'}
      </td>
    </tr>
  `).join('')

  // Build a sensible filename: lost-receipts_<client-or-request>_<YYYY-MM-DD>
  // Browser print dialogs use document.title as the default save name.
  const dateStr = new Date().toISOString().slice(0, 10)
  const slug = (request.clients?.name || request.title || 'request')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40)
  const pdfFilename = `lost-receipts_${slug}_${dateStr}`

  const html = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8">
  <title>${pdfFilename}</title>
  <style>
    @media print {
      .no-print { display: none !important; }
      body { margin: 0; }
      @page { margin: 18mm 16mm; size: A4; }
    }
    body { font-family: Georgia, serif; max-width: 860px; margin: 40px auto; padding: 0 32px; color: #0f1117; font-size: 14px; line-height: 1.5; }
    .print-bar { display: flex; align-items: center; justify-content: space-between; margin-bottom: 28px; gap: 16px; flex-wrap: wrap; }
    .print-btn { display: inline-flex; align-items: center; gap: 8px; padding: 11px 22px; background: #489291; color: white; border: none; border-radius: 8px; font-size: 14px; cursor: pointer; font-family: inherit; font-weight: 500; }
    .print-btn:hover { background: #37706f; }
    .print-hint { font-size: 12px; color: #5a5f72; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; }
    h1 { font-size: 22px; margin-bottom: 4px; font-weight: normal; }
    .meta { color: #5a5f72; font-size: 13px; margin-bottom: 24px; }
    .meta strong { color: #0f1117; }
    .notice { background: #fffbeb; border: 1px solid #d97706; border-radius: 6px; padding: 12px 16px; font-size: 13px; margin-bottom: 24px; color: #92400e; }
    table { width: 100%; border-collapse: collapse; font-size: 13px; }
    th { background: #f7f6f2; padding: 9px 12px; text-align: left; border-bottom: 2px solid #e4e1dc; font-size: 11px; text-transform: uppercase; letter-spacing: 0.05em; font-weight: 600; }
    td { padding: 10px 12px; border-bottom: 1px solid #eeecea; vertical-align: top; }
    tr:last-child td { border-bottom: none; }
    .footer { margin-top: 36px; font-size: 11px; color: #9ba1b4; border-top: 1px solid #eeecea; padding-top: 14px; }
  </style>
</head>
<body>
  <div class="print-bar no-print">
    <button class="print-btn" onclick="window.print()">📄 Save as PDF</button>
    <span class="print-hint">In the print dialog, choose <strong>"Save as PDF"</strong> as the destination.</span>
  </div>
  <h1>Lost Receipt Declaration</h1>
  <div class="meta">
    Client: <strong>${request.clients?.name || '—'}</strong> &nbsp;·&nbsp;
    Request: <strong>${request.title}</strong> &nbsp;·&nbsp;
    Generated: <strong>${new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' })}</strong>
  </div>
  <div class="notice">⚠ This document records transactions for which the original receipt or invoice cannot be found. It has been completed by the client and may be retained as supporting evidence in bookkeeping records.</div>
  <table>
    <thead>
      <tr>
        <th style="width:90px">Date</th>
        <th>Description</th>
        <th style="width:90px;text-align:right">Amount</th>
        <th>Client explanation</th>
        <th style="width:90px;text-align:center">Wholly business?</th>
      </tr>
    </thead>
    <tbody>${rows}</tbody>
  </table>
  <div class="footer">Generated by Receipt Chaser &nbsp;·&nbsp; ${new Date().toLocaleString('en-GB')}</div>
</body>
</html>`

  const blob = new Blob([html], { type: 'text/html' })
  const url = URL.createObjectURL(blob)
  window.open(url, '_blank')
  setTimeout(() => URL.revokeObjectURL(url), 30000)
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function RequestDetail() {
  const { requestId } = useParams()
  const navigate = useNavigate()
  const toast = useToast()
  const { role } = usePractice()
  usePageTitle('Request')
  const canDelete = role !== 'assistant'
  const [request, setRequest] = useState(null)
  const [loading, setLoading] = useState(true)
  const [editingNotes, setEditingNotes] = useState(false)
  const [notes, setNotes] = useState('')
  const [savingNotes, setSavingNotes] = useState(false)
  const [selected, setSelected] = useState(new Set())
  // Which transaction's bookkeeper note is currently being edited (null = none)
  const [editingNoteTxId, setEditingNoteTxId] = useState(null)
  const [bulkDeleting, setBulkDeleting] = useState(false)
  const [bulkDownloading, setBulkDownloading] = useState(false)

  // ─── Cancel/reopen request state ────────────────────────────────────────
  // cancelConfirming: true after the first Cancel click; the button text
  //                   flips to "Confirm cancel" and a second click commits.
  //                   Auto-resets after 5 seconds of inactivity so the user
  //                   doesn't accidentally cancel later by clicking the
  //                   primed button.
  const [cancelConfirming, setCancelConfirming] = useState(false)
  const [cancellingRequest, setCancellingRequest] = useState(false)
  const [cleaningUp, setCleaningUp] = useState(false)
  const [attachmentCache, setAttachmentCache] = useState({})
  const [sort, setSort] = useState({ field: 'sort_order', dir: 'asc' })
  const [settings, setSettings] = useState(null)
  const [sendingEmail, setSendingEmail] = useState(false)
  const [emailSent, setEmailSent] = useState(false)

  // ─── Per-request reminder edit state ────────────────────────────────────────
  // Editing is inline: when the user clicks "Edit", form fields appear in
  // place of the read-only summary. State holds the draft values until they
  // click Save.
  const [editingReminder, setEditingReminder]       = useState(false)
  const [reminderOnDraft, setReminderOnDraft]       = useState(true)
  const [reminderCustomDraft, setReminderCustomDraft] = useState(false)
  const [reminderDaysDraft, setReminderDaysDraft]   = useState('7')
  const [savingReminder, setSavingReminder]         = useState(false)

  // ─── Table filter ────────────────────────────────────────────────────────
  // Filters the transaction table by status. 'all' shows everything.
  const [statusFilter, setStatusFilter] = useState('all')

  // ─── Add-transactions modal ──────────────────────────────────────────────
  // showAddModal: true when the modal is open
  // addMode: 'manual' or 'csv' (which sub-tab inside the modal is active)
  // manualRows: array of { date, description, amount, payee } for the
  //             manual-entry form
  // csvParsedRows: rows parsed from an uploaded CSV file, awaiting confirm
  // addingTransactions: true while the save is in flight
  const [showAddModal, setShowAddModal]         = useState(false)
  const [addMode, setAddMode]                   = useState('manual')
  const [manualRows, setManualRows]             = useState([blankManualRow()])
  const [csvParsedRows, setCsvParsedRows]       = useState(null)
  // Raw rows handed to the custom-mapping UI when auto-detect fails to
  // recognise the file. Null means "no mapping in progress".
  const [csvMappingRows, setCsvMappingRows]     = useState(null)
  const [csvParsing, setCsvParsing]             = useState(false)
  const [addingTransactions, setAddingTransactions] = useState(false)

  // ─── Bulk description editor ─────────────────────────────────────────────
  // Opens a panel showing the selected rows, each with its description in
  // an editable field. Save commits all edits in one go.
  const [showDescriptionEditor, setShowDescriptionEditor] = useState(false)
  const [descriptionDrafts, setDescriptionDrafts]         = useState({}) // { txId: newDescription }
  const [savingDescriptions, setSavingDescriptions]       = useState(false)

  const load = useCallback(async () => {
    const { data, error } = await getRequestById(requestId)
    if (error) toast(error.message, 'error')
    else { setRequest(data); setNotes(data?.bookkeeper_notes || '') }
    setLoading(false)
    setSelected(new Set())
  }, [requestId])

  // Fetch bookkeeper settings once we have the request (need bookkeeper_id)
  useEffect(() => {
    if (!request) return
    getSettings(request.bookkeeper_id).then(({ data }) => setSettings(data || {}))
  }, [request?.bookkeeper_id])

  useEffect(() => { load() }, [load])

  useEffect(() => {
    if (!request) return
    const uploadedTxs = (request.transactions || []).filter(t => t.status === 'uploaded')
    uploadedTxs.forEach(async (tx) => {
      if (!attachmentCache[tx.id]) {
        const { data } = await getAttachments(tx.id)
        setAttachmentCache(prev => ({ ...prev, [tx.id]: data || [] }))
      }
    })
  }, [request])

  const handleSort = (field, dir) => setSort({ field, dir })

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }

  const toggleAll = (txs) => {
    setSelected(selected.size === txs.length ? new Set() : new Set(txs.map(t => t.id)))
  }

  const handleBulkDelete = async (txs) => {
    const selTxs = txs.filter(t => selected.has(t.id))
    if (!window.confirm(`Delete ${selTxs.length} transaction${selTxs.length !== 1 ? 's' : ''}? Any uploaded files will also be removed.`)) return
    setBulkDeleting(true)
    for (const tx of selTxs) await deleteTransaction(tx.id)
    setBulkDeleting(false)
    toast(`${selTxs.length} transaction${selTxs.length !== 1 ? 's' : ''} deleted`, 'default')
    load()
  }

  // Downloads attachments for the given transaction IDs into a single zip file.
  // Used by both "Download as zip" (selected) and "Download all" (all uploaded).
  // When `includeFullSummary` is true, also adds a summary CSV covering ALL
  // transactions in the request (not just the ones being downloaded).
  const downloadTransactionsAsZip = async (txIds, { includeFullSummary = false } = {}) => {
    setBulkDownloading(true)
    toast('Preparing zip file…', 'default')

    try {
      const zip = new JSZip()
      let fileCount = 0
      const usedNames = new Set()
      // Track attachments per transaction so we can write them into the CSV
      const attachmentsByTxId = {}

      for (const txId of txIds) {
        // Find the matching transaction so we can build a sensible filename
        const tx = (request.transactions || []).find(t => t.id === txId)

        let attachments = attachmentCache[txId]
        if (!attachments) {
          const { data } = await getAttachments(txId)
          attachments = data || []
          setAttachmentCache(prev => ({ ...prev, [txId]: attachments }))
        }
        attachmentsByTxId[txId] = attachments

        for (const a of attachments) {
          try {
            // Mint a fresh signed URL for the fetch. The file_url stored
            // on the row is a public URL that will stop working after
            // Commit B locks down the bucket. Generate fresh each time
            // so this code path works regardless of bucket state.
            const { signedUrl } = await getReceiptSignedUrl(a.file_path)
            if (!signedUrl) continue
            const response = await fetch(signedUrl)
            const blob = await response.blob()

            // Rename the file based on its transaction (date / vendor / amount).
            // Fall back to the original filename if we have no transaction context.
            let fileName = tx
              ? buildReceiptFilename(tx, a.file_name)
              : a.file_name

            // Deduplicate within the zip — same vendor on same day at same
            // amount could happen (e.g. multiple coffee receipts).
            if (usedNames.has(fileName)) {
              const ext = fileName.includes('.') ? '.' + fileName.split('.').pop() : ''
              const base = fileName.slice(0, fileName.length - ext.length)
              let i = 2
              while (usedNames.has(`${base} (${i})${ext}`)) i++
              fileName = `${base} (${i})${ext}`
            }
            usedNames.add(fileName)

            zip.file(fileName, blob)
            fileCount++
          } catch {
            // Skip files that fail to fetch
          }
        }
      }

      if (fileCount === 0 && !includeFullSummary) {
        toast('No uploaded files found', 'error')
        setBulkDownloading(false)
        return
      }

      // For the full-export action, also add CSV summary covering every
      // transaction — including pending and lost ones, with their notes.
      if (includeFullSummary) {
        // Make sure attachments are loaded for every transaction so the CSV
        // can list the renamed filenames against all uploaded ones.
        for (const tx of (request.transactions || [])) {
          if (!attachmentsByTxId[tx.id]) {
            const cached = attachmentCache[tx.id]
            if (cached) {
              attachmentsByTxId[tx.id] = cached
            } else {
              const { data } = await getAttachments(tx.id)
              attachmentsByTxId[tx.id] = data || []
              setAttachmentCache(prev => ({ ...prev, [tx.id]: data || [] }))
            }
          }
        }

        const csv = buildSummaryCSV(request.transactions || [], attachmentsByTxId)
        // BOM so Excel opens it as UTF-8 (handles £ and other non-ASCII correctly)
        zip.file('summary.csv', '\uFEFF' + csv)
      }

      const content = await zip.generateAsync({ type: 'blob' })
      const url = URL.createObjectURL(content)
      const a = document.createElement('a')
      a.href = url
      a.download = `receipts-${request.title.replace(/[^a-z0-9]/gi, '-').toLowerCase()}.zip`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)

      const summaryNote = includeFullSummary ? ' (with summary.csv)' : ''
      toast(`Downloaded ${fileCount} file${fileCount !== 1 ? 's' : ''} as zip${summaryNote} ✓`, 'success')
    } catch (err) {
      toast('Download failed: ' + err.message, 'error')
    }

    setBulkDownloading(false)
  }

  // Download zip of receipts for currently-selected transactions
  const handleBulkDownload = () => downloadTransactionsAsZip([...selected])

  // Download zip of receipts for ALL transactions that have at least one attachment
  const handleDownloadAll = async () => {
    // Build the list of transaction IDs that have uploaded attachments.
    // We use the transaction status as a fast first check, then verify
    // against the attachment cache where loaded.
    const uploadedTxs = (request.transactions || []).filter(t => t.status === 'uploaded')
    if (uploadedTxs.length === 0) {
      toast('No uploaded receipts to download', 'default')
      return
    }
    await downloadTransactionsAsZip(uploadedTxs.map(t => t.id), { includeFullSummary: true })
  }

  const handleSendEmail = async () => {
    const clientEmail = request.clients?.email
    if (!clientEmail) { toast('No email address on file for this client', 'error'); return }
    setSendingEmail(true)
    const link = `${window.location.origin}/request/${request.token}`
    const { error } = await sendClientRequestEmail({
      clientEmail,
      clientName:      request.clients?.name,
      // Fallback chain mirrors the reminder worker: prefer display_name,
      // then practice_name, then full_name. Passing null caused the backend
      // default 'Your bookkeeper' to leak into the body in two places (once
      // for the {{bookkeeper_name}} mention, once for the sign-off).
      bookkeeperName:  settings?.display_name
                    || settings?.practice_name
                    || settings?.full_name
                    || 'Your bookkeeper',
      requestTitle:    request.title,
      requestLink:     link,
      subjectTemplate: settings?.email_subject_template || null,
      bodyTemplate:    settings?.email_body_template || null,
    })
    setSendingEmail(false)
    if (error) {
      toast('Email failed to send: ' + error.message, 'error')
    } else {
      setEmailSent(true)
      toast(`Email sent to ${clientEmail} ✓`, 'success')
      setTimeout(() => setEmailSent(false), 4000)
    }
  }

  const handleCleanupFiles = async () => {
    if (!window.confirm(
      `Permanently delete all uploaded files from "${request.title}"?\n\n` +
      `This cannot be undone. Only do this after you have saved the files to your own system.`
    )) return
    setCleaningUp(true)
    const { data, error } = await cleanupRequestFiles(requestId)
    setCleaningUp(false)
    if (error) { toast(error.message, 'error'); return }
    const count = data?.removed || 0
    toast(`${count} file${count !== 1 ? 's' : ''} removed from storage ✓`, 'success')
    setAttachmentCache({})
    load()
  }

  // ─── Reminder edit handlers ─────────────────────────────────────────────────
  // Populate draft state from the current request when the user clicks Edit.
  // reminder_days_after_send is NULL when the request follows the practice
  // default; we surface that as "use practice default" rather than showing
  // a blank input.
  const handleStartEditReminder = () => {
    if (!request) return
    setReminderOnDraft(request.reminder_enabled !== false)
    const hasCustomDays = typeof request.reminder_days_after_send === 'number'
    setReminderCustomDraft(hasCustomDays)
    setReminderDaysDraft(String(
      hasCustomDays
        ? request.reminder_days_after_send
        : (settings?.reminder_days_after_send ?? 7)
    ))
    setEditingReminder(true)
  }

  const handleCancelEditReminder = () => {
    setEditingReminder(false)
  }

  const handleSaveReminder = async () => {
    const payload = { enabled: reminderOnDraft }
    if (reminderOnDraft) {
      if (reminderCustomDraft) {
        const n = parseInt(reminderDaysDraft, 10)
        if (Number.isNaN(n) || n < 1 || n > 90) {
          toast('Reminder schedule must be between 1 and 90 days', 'error')
          return
        }
        payload.daysAfterSend = n
      } else {
        // Falling back to practice default = clear the per-request override
        payload.daysAfterSend = null
      }
    } else {
      // Reminders off for this request: don't bother updating the day count
    }

    setSavingReminder(true)
    const { error } = await updateRequestReminderSettings(requestId, payload)
    setSavingReminder(false)
    if (error) { toast(error.message, 'error'); return }
    toast('Reminder settings saved ✓', 'success')
    setEditingReminder(false)
    load() // refresh the request to show the new values
  }

  // ─── Add transactions handlers ──────────────────────────────────────────
  // The modal supports two input modes:
  //   - 'manual' : a small grid of editable rows the user fills in by hand
  //   - 'csv'    : drop a CSV file, parse it with the shared parser, preview
  //                what was read, then commit on confirmation
  // Both modes funnel through addTransactionsToRequest() on save.

  const openAddModal = () => {
    setManualRows([blankManualRow()])
    setCsvParsedRows(null)
    setCsvMappingRows(null)
    setAddMode('manual')
    setShowAddModal(true)
  }

  const closeAddModal = () => {
    if (addingTransactions) return // don't close mid-save
    setShowAddModal(false)
    setCsvMappingRows(null)
  }

  const handleAddManualRow = () => {
    setManualRows(rows => [...rows, blankManualRow()])
  }

  const handleManualRowChange = (idx, field, value) => {
    setManualRows(rows => rows.map((r, i) => i === idx ? { ...r, [field]: value } : r))
  }

  const handleRemoveManualRow = (idx) => {
    setManualRows(rows => {
      const next = rows.filter((_, i) => i !== idx)
      // Always keep at least one editable row so the form never collapses
      return next.length > 0 ? next : [blankManualRow()]
    })
  }

  const handleCsvFile = async (file) => {
    if (!file || !file.name.endsWith('.csv')) {
      toast('Please upload a .csv file', 'error')
      return
    }
    setCsvParsing(true)
    try {
      const result = await parseCSV(file, 'auto')
      if (!result.transactions || result.transactions.length === 0) {
        // Auto-detect failed. Fall back to the custom mapper using the raw
        // rows that were already parsed. The mapper renders inline in the
        // CSV tab instead of the upload pane.
        toast(
          "Couldn't read this file automatically. Map the columns yourself below.",
          'default'
        )
        setCsvParsedRows(null)
        setCsvMappingRows(result.rawRows || [])
      } else {
        setCsvParsedRows(result.transactions)
        setCsvMappingRows(null)
      }
    } catch (err) {
      toast(err.message || 'Could not parse CSV', 'error')
      setCsvParsedRows(null)
      setCsvMappingRows(null)
    } finally {
      setCsvParsing(false)
    }
  }

  // Called when the user confirms a custom mapping inside the modal.
  // Treats the resulting transactions exactly like an auto-detected upload.
  const handleCsvMappingApply = (transactions /* , mapping */) => {
    setCsvParsedRows(transactions)
    setCsvMappingRows(null)
  }

  const handleCsvMappingCancel = () => {
    setCsvMappingRows(null)
  }

  const handleSaveNewTransactions = async () => {
    // Filter out empty manual rows so we don't insert blanks. A row counts
    // as filled-in if it has any of description, amount, or date.
    let toAdd = []
    if (addMode === 'manual') {
      const filled = manualRows.filter(r => r.description.trim() || r.amount || r.date)
      if (filled.length === 0) {
        toast('Add at least one transaction first', 'error')
        return
      }
      // Convert the type + positive-amount the user typed into a signed
      // amount. Expenditure becomes negative, income stays positive. This
      // is the convention used everywhere else in the app (see the Type
      // column rendering, which checks amount > 0).
      toAdd = filled.map(r => {
        const abs = Math.abs(parseFloat(r.amount) || 0)
        const signed = r.type === 'income' ? abs : -abs
        return { ...r, amount: signed }
      })
    } else if (addMode === 'csv') {
      if (!csvParsedRows || csvParsedRows.length === 0) {
        toast('Upload a CSV first', 'error')
        return
      }
      toAdd = csvParsedRows
    }

    setAddingTransactions(true)
    const { error } = await addTransactionsToRequest(requestId, toAdd)
    setAddingTransactions(false)
    if (error) { toast(error.message, 'error'); return }

    toast(`${toAdd.length} transaction${toAdd.length === 1 ? '' : 's'} added ✓`, 'success')
    setShowAddModal(false)
    load() // refresh to show the new rows
  }

  // ─── Bulk description edit handlers ─────────────────────────────────────

  const openDescriptionEditor = () => {
    if (selected.size === 0) {
      toast('Select at least one transaction first', 'error')
      return
    }
    // Seed drafts with current values so the user starts from what's there
    const seedDrafts = {}
    for (const id of selected) {
      const tx = (request?.transactions || []).find(t => t.id === id)
      if (tx) seedDrafts[id] = tx.description || ''
    }
    setDescriptionDrafts(seedDrafts)
    setShowDescriptionEditor(true)
  }

  const closeDescriptionEditor = () => {
    if (savingDescriptions) return
    setShowDescriptionEditor(false)
  }

  const handleDescriptionDraftChange = (id, value) => {
    setDescriptionDrafts(d => ({ ...d, [id]: value }))
  }

  const handleSaveDescriptions = async () => {
    // Only send rows that actually changed, to avoid no-op writes
    const changed = {}
    for (const [id, newDesc] of Object.entries(descriptionDrafts)) {
      const tx = (request?.transactions || []).find(t => t.id === id)
      const oldDesc = tx?.description || ''
      if (newDesc !== oldDesc) changed[id] = newDesc
    }
    if (Object.keys(changed).length === 0) {
      toast('No changes to save', 'default')
      setShowDescriptionEditor(false)
      return
    }
    setSavingDescriptions(true)
    const { error } = await updateTransactionDescriptions(changed)
    setSavingDescriptions(false)
    if (error) { toast(error.message, 'error'); return }
    toast(`${Object.keys(changed).length} description${Object.keys(changed).length === 1 ? '' : 's'} updated ✓`, 'success')
    setShowDescriptionEditor(false)
    setSelected(new Set())
    load()
  }

  const handleDeleteRequest = async () => {
    if (!window.confirm(`Delete "${request.title}"? This will remove all transactions and uploaded files.`)) return
    const { error } = await deleteRequest(requestId)
    if (error) { toast(error.message, 'error'); return }
    toast('Request deleted', 'default')
    navigate(`/clients/${request.client_id}`)
  }

  // ─── Cancel and reopen ──────────────────────────────────────────────────
  // Two-step cancel: first click primes (cancelConfirming = true and the
  // button text changes), second click commits. The primed state auto-resets
  // after 5 seconds so a stale primed button doesn't lurk.
  const handleCancelClick = async () => {
    if (!cancelConfirming) {
      setCancelConfirming(true)
      // Reset after 5 seconds if they don't click again
      setTimeout(() => setCancelConfirming(false), 5000)
      return
    }
    // Second click: actually cancel
    setCancellingRequest(true)
    const { error } = await cancelRequest(requestId)
    setCancellingRequest(false)
    setCancelConfirming(false)
    if (error) { toast(error.message, 'error'); return }
    toast('Request cancelled ✓', 'success')
    load()
  }

  const handleReopenClick = async () => {
    setCancellingRequest(true)
    const { error } = await reopenRequest(requestId)
    setCancellingRequest(false)
    if (error) { toast(error.message, 'error'); return }
    toast('Request reopened ✓', 'success')
    load()
  }

  const handleDeleteSingle = async (tx) => {
    if (!window.confirm(`Remove "${tx.description}"? Any uploaded files will also be deleted.`)) return
    const { error } = await deleteTransaction(tx.id)
    if (error) { toast(error.message, 'error'); return }
    toast('Transaction removed', 'default')
    load()
  }

  const handleSaveNotes = async () => {
    setSavingNotes(true)
    const { error } = await updateRequestNotes(requestId, notes)
    setSavingNotes(false)
    if (error) { toast(error.message, 'error'); return }
    toast('Notes saved', 'success')
    setEditingNotes(false)
    load()
  }

  if (loading) return (
    <><Navbar /><div className="loading-screen"><div className="spinner" /></div></>
  )
  if (!request) return (
    <><Navbar /><div className="container" style={{ padding: '80px 24px', textAlign: 'center' }}><h2>Request not found</h2></div></>
  )

  // Cancelled state drives a lot of conditional UI below: cancellation
  // banner shown, reminder card hidden, add-transactions button hidden,
  // edit-descriptions button hidden, send-email button hidden. The original
  // data stays viewable (transactions, uploads, notes, download zip), since
  // the whole point of "cancel not delete" is to preserve the record.
  const isCancelled = !!request.cancelled_at

  const rawTxs = [...(request.transactions || [])]
  // Apply the user's filter dropdown choice before sorting and rendering.
  // The status counts shown in the dropdown labels use the unfiltered list,
  // so the user always sees "Pending (12)" not "Pending (0)" when they have
  // it filtered to uploaded only.
  const filteredTxs = statusFilter === 'all'
    ? rawTxs
    : rawTxs.filter(t => (t.status || 'pending') === statusFilter)
  const txs = sortRows(filteredTxs, sort)
  const total = rawTxs.length
  const uploaded = rawTxs.filter(t => t.status === 'uploaded').length
  const lost = rawTxs.filter(t => t.status === 'lost').length
  const pending = rawTxs.filter(t => (t.status || 'pending') === 'pending').length
  const resolved = uploaded + lost
  const someSelected = selected.size > 0
  const allSelected = selected.size === txs.length && txs.length > 0
  const selectedHaveUploads = [...selected].some(id => (attachmentCache[id] || []).length > 0)

  const statusStyle = (tx) => {
    if (tx.status === 'uploaded') return { bg: 'var(--green-light)', color: 'var(--green)', icon: '✓', label: 'Uploaded' }
    if (tx.status === 'lost')     return { bg: 'var(--amber-light)', color: 'var(--amber)', icon: '⚠', label: 'Lost' }
    return                               { bg: 'var(--surface-2)',   color: 'var(--ink-muted)', icon: '⏳', label: 'Pending' }
  }

  return (
    <>
      <Navbar />
      <PageHeader
        back={{ label: request.clients?.name, to: `/clients/${request.client_id}` }}
        title={request.title}
        subtitle={`${request.clients?.name} · Created ${formatDate(request.created_at)}`}
        actions={
          <>
            {request.clients?.email && !isCancelled && (
              <button
                className="btn btn-primary"
                onClick={handleSendEmail}
                disabled={sendingEmail}
                title={`Send request email to ${request.clients.email}`}
              >
                {sendingEmail ? 'Sending…' : emailSent ? '✓ Sent!' : '📧 Send email'}
              </button>
            )}
            {lost > 0 && (
              <button className="btn btn-secondary" onClick={() => openLostReceiptsPrintView(request)}>
                📄 Lost receipts (PDF)
              </button>
            )}
            {pending === 0 && uploaded > 0 && (
              <button
                className="btn btn-secondary"
                onClick={handleCleanupFiles}
                disabled={cleaningUp}
                title="Permanently delete uploaded files once you've saved them to your own system"
              >
                {cleaningUp ? 'Deleting…' : '🗂 Permanently delete stored files'}
              </button>
            )}
            <CopyLinkBtn token={request.token} />
            {/* Cancel: only when not already cancelled. Two-step: first click
                primes (button text flips), second click commits. State auto-
                resets after 5 seconds. The button is styled neutrally so it
                doesn't read as destructive (cancellation is reversible). */}
            {!isCancelled && (
              <button
                className="btn btn-secondary btn-sm"
                onClick={handleCancelClick}
                disabled={cancellingRequest}
                style={cancelConfirming ? {
                  background: 'var(--amber)',
                  color: 'white',
                  borderColor: 'var(--amber)',
                } : undefined}
              >
                {cancellingRequest
                  ? 'Cancelling…'
                  : cancelConfirming
                    ? 'Confirm cancel'
                    : '⊘ Cancel request'}
              </button>
            )}
            {canDelete && (
              <button className="btn btn-danger btn-sm" onClick={handleDeleteRequest}>🗑 Delete request</button>
            )}
          </>
        }
      />
      <main className="container" style={{ padding: '0 24px 40px' }}>

        {/* Cancellation banner. Shown when the request has been cancelled.
            Uploaded receipts are preserved (visible in the table below), but
            new uploads from the client are blocked and reminders are off. */}
        {isCancelled && (
          <div style={{
            background: 'var(--amber-light)',
            border: '1px solid var(--amber)',
            borderRadius: 'var(--radius)',
            padding: '14px 18px',
            marginBottom: 16,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            flexWrap: 'wrap',
          }}>
            <div style={{ fontSize: '0.9rem', lineHeight: 1.5 }}>
              <strong style={{ color: 'var(--ink)' }}>This request is cancelled.</strong>{' '}
              <span style={{ color: 'var(--ink-muted)' }}>
                Uploaded receipts are preserved, but the client can no
                longer upload anything new and no reminders will be sent.
                Cancelled on {new Date(request.cancelled_at).toLocaleDateString('en-GB', {
                  day: '2-digit', month: 'short', year: 'numeric',
                })}.
              </span>
            </div>
            <button
              className="btn btn-secondary btn-sm"
              onClick={handleReopenClick}
              disabled={cancellingRequest}
            >
              {cancellingRequest ? 'Reopening…' : 'Reopen request'}
            </button>
          </div>
        )}

        {/* Progress */}
        <div className="card fade-up-2" style={{ padding: '18px 24px', marginBottom: 16 }}>
          <div className="flex-between" style={{ marginBottom: 10, flexWrap: 'wrap', gap: 8 }}>
            <span style={{ fontWeight: 500, fontSize: '0.9rem' }}>Progress</span>
            <div style={{ display: 'flex', gap: 16, fontSize: '0.82rem', alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{ color: 'var(--green)' }}>✓ {uploaded} uploaded</span>
              {lost > 0 && <span style={{ color: 'var(--amber)' }}>⚠ {lost} lost</span>}
              {pending > 0 && <span style={{ color: 'var(--ink-muted)' }}>⏳ {pending} pending</span>}
              {uploaded > 0 && (
                <button
                  className="btn btn-sm"
                  style={{ background: 'var(--green)', color: 'white', border: 'none' }}
                  onClick={handleDownloadAll}
                  disabled={bulkDownloading}
                >
                  {bulkDownloading ? '⏳ Building zip…' : `⬇ Download all (${uploaded})`}
                </button>
              )}
            </div>
          </div>
          <div style={{ height: 8, background: 'var(--surface-2)', borderRadius: 100, overflow: 'hidden' }}>
            <div style={{
              height: '100%', width: `${total ? (resolved / total) * 100 : 0}%`,
              background: resolved === total ? 'var(--green)' : 'var(--accent)',
              borderRadius: 100, transition: 'width 0.4s ease',
            }} />
          </div>
        </div>

        {/* Reminder + notes laid out side by side. Both cards used to stack
            vertically, which pushed the transaction table well below the
            fold on smaller laptops. Flex with wrap means: side-by-side on
            wide screens, stacked on narrow.

            The reminder card is hidden on complete requests (every transaction
            uploaded or marked lost) and on cancelled requests, since neither
            is actionable anymore. When that happens, the notes card naturally
            expands to the full row width. */}
        <div
          className="fade-up-2"
          style={{
            display: 'flex', gap: 16, marginBottom: 16, flexWrap: 'wrap',
          }}
        >
          {pending > 0 && !isCancelled && (
            <div style={{ flex: '1 1 320px', minWidth: 0 }}>
              <RequestReminderCard
                request={request}
                settings={settings}
                editing={editingReminder}
                onStartEdit={handleStartEditReminder}
                onCancel={handleCancelEditReminder}
                onSave={handleSaveReminder}
                saving={savingReminder}
                reminderOnDraft={reminderOnDraft}
                setReminderOnDraft={setReminderOnDraft}
                reminderCustomDraft={reminderCustomDraft}
                setReminderCustomDraft={setReminderCustomDraft}
                reminderDaysDraft={reminderDaysDraft}
                setReminderDaysDraft={setReminderDaysDraft}
                inlineCard
              />
            </div>
          )}
          {/* Bookkeeper notes. inlineCard variant: no own marginBottom (the
              flex parent provides spacing instead) so cards align cleanly. */}
          <div style={{ flex: '1 1 320px', minWidth: 0 }}>
            <div className="card" style={{ padding: '18px 24px', marginBottom: 0 }}>
              <div className="flex-between" style={{ marginBottom: editingNotes ? 10 : 0 }}>
                <span style={{ fontWeight: 500, fontSize: '0.88rem' }}>📝 Notes for client</span>
                {!editingNotes && (
                  <button className="btn btn-ghost btn-sm" onClick={() => setEditingNotes(true)}>
                    {request.bookkeeper_notes ? 'Edit' : '+ Add notes'}
                  </button>
                )}
              </div>
              {editingNotes ? (
                <>
                  <textarea
                    className="input"
                    value={notes}
                    onChange={e => setNotes(e.target.value)}
                    rows={3}
                    placeholder="Notes visible to your client on their portal…"
                    style={{ resize: 'vertical', marginTop: 8 }}
                    autoFocus
                  />
                  <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                    <button className="btn btn-primary btn-sm" onClick={handleSaveNotes} disabled={savingNotes}>
                      {savingNotes ? 'Saving…' : 'Save'}
                    </button>
                    <button className="btn btn-ghost btn-sm" onClick={() => { setEditingNotes(false); setNotes(request.bookkeeper_notes || '') }}>
                      Cancel
                    </button>
                  </div>
                </>
              ) : (
                request.bookkeeper_notes
                  ? <p style={{ color: 'var(--ink-muted)', fontSize: '0.86rem', marginTop: 8, lineHeight: 1.6 }}>{request.bookkeeper_notes}</p>
                  : <p style={{ color: 'var(--ink-faint)', fontSize: '0.83rem', marginTop: 4 }}>No notes added yet.</p>
              )}
            </div>
          </div>
        </div>

        {/* Bulk action bar */}
        {someSelected && (
          <div className="fade-up" style={{
            display: 'flex', alignItems: 'center', gap: 12,
            background: 'var(--ink)', color: 'white',
            padding: '12px 20px', borderRadius: 'var(--radius)',
            marginBottom: 12, flexWrap: 'wrap',
          }}>
            <span style={{ fontSize: '0.88rem', fontWeight: 500 }}>
              {selected.size} transaction{selected.size !== 1 ? 's' : ''} selected
            </span>
            {selectedHaveUploads && (
              <button
                className="btn btn-sm"
                style={{ background: 'var(--green)', color: 'white', border: 'none' }}
                onClick={handleBulkDownload}
                disabled={bulkDownloading}
              >
                {bulkDownloading ? '⏳ Building zip…' : '⬇ Download as zip'}
              </button>
            )}
            {!isCancelled && (
              <button
                className="btn btn-sm"
                style={{ background: 'rgba(255,255,255,0.15)', color: 'white', border: '1px solid rgba(255,255,255,0.25)' }}
                onClick={openDescriptionEditor}
              >
                ✏️ Edit descriptions
              </button>
            )}
            {canDelete && (
              <button
                className="btn btn-sm"
                style={{ background: '#dc2626', color: 'white', border: 'none' }}
                onClick={() => handleBulkDelete(txs)}
                disabled={bulkDeleting}
              >
                {bulkDeleting ? 'Deleting…' : '🗑 Delete selected'}
              </button>
            )}
            <button
              className="btn btn-ghost btn-sm"
              style={{ color: 'rgba(255,255,255,0.6)' }}
              onClick={() => setSelected(new Set())}
            >
              Cancel
            </button>
          </div>
        )}

        {/* Transactions table */}
        <div className="card fade-up-3">
          {/* Toolbar: status filter on the left, add-transactions button on the right.
              Compact single line so the page doesn't grow vertically just for this. */}
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            gap: 12, padding: '12px 16px',
            borderBottom: '1px solid var(--surface-2)',
            flexWrap: 'wrap',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <label htmlFor="status-filter" style={{
                fontSize: '0.82rem', color: 'var(--ink-muted)', fontWeight: 500,
              }}>
                Showing
              </label>
              <select
                id="status-filter"
                value={statusFilter}
                onChange={e => { setStatusFilter(e.target.value); setSelected(new Set()) }}
                className="input"
                style={{
                  height: 32, padding: '4px 10px',
                  fontSize: '0.85rem', width: 'auto',
                }}
              >
                <option value="all">All ({total})</option>
                <option value="pending">Pending ({pending})</option>
                <option value="uploaded">Uploaded ({uploaded})</option>
                <option value="lost">Lost ({lost})</option>
              </select>
              {statusFilter !== 'all' && txs.length === 0 && (
                <span style={{ fontSize: '0.8rem', color: 'var(--ink-faint)', fontStyle: 'italic' }}>
                  No transactions match this filter
                </span>
              )}
            </div>
            {!isCancelled && (
              <button
                className="btn btn-secondary btn-sm"
                onClick={openAddModal}
              >
                + Add transactions
              </button>
            )}
          </div>

          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th style={{ width: 36 }}>
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={() => toggleAll(txs)}
                      style={{ width: 15, height: 15, cursor: 'pointer', accentColor: 'var(--accent)' }}
                      title="Select all"
                    />
                  </th>
                  <SortTh label="Status"      field="status"      sort={sort} onSort={handleSort} />
                  <SortTh label="Date"        field="date"        sort={sort} onSort={handleSort} />
                  <SortTh label="Description" field="description" sort={sort} onSort={handleSort} />
                  <SortTh label="Type"        field="type"        sort={sort} onSort={handleSort} />
                  <SortTh label="Amount"      field="amount"      sort={sort} onSort={handleSort} style={{ textAlign: 'right' }} />
                  <th style={{ width: 36 }}></th>
                </tr>
              </thead>
              <tbody>
                {txs.map(tx => {
                  const s = statusStyle(tx)
                  const isSelected = selected.has(tx.id)
                  // Status-based row tint mirrors the client portal: faint
                  // green for uploaded, faint amber for lost, transparent for
                  // pending. Selection highlight takes precedence so the user
                  // can see what they're acting on regardless of status.
                  const statusBg = tx.status === 'uploaded'
                    ? 'rgba(34,160,107,0.08)'
                    : tx.status === 'lost'
                      ? 'rgba(217,119,6,0.06)'
                      : undefined
                  const rowBg = isSelected ? 'var(--accent-light)' : statusBg
                  return (
                    <tr key={tx.id} style={{ background: rowBg }}>
                      <td>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleSelect(tx.id)}
                          style={{ width: 15, height: 15, cursor: 'pointer', accentColor: 'var(--accent)' }}
                        />
                      </td>
                      <td>
                        <span style={{
                          display: 'inline-flex', alignItems: 'center', gap: 4,
                          padding: '3px 9px', borderRadius: 100,
                          fontSize: '0.73rem', fontWeight: 600,
                          background: s.bg, color: s.color, whiteSpace: 'nowrap',
                        }}>
                          {s.icon} {s.label}
                        </span>
                      </td>
                      <td style={{ color: 'var(--ink-muted)', whiteSpace: 'nowrap', fontSize: '0.83rem' }}>
                        {formatDate(tx.date)}
                      </td>
                      <td style={{ maxWidth: 260 }}>
                        <div style={{ fontWeight: 500, fontSize: '0.86rem', marginBottom: 2 }}>{tx.description}</div>
                        {tx.status === 'lost' && tx.lost_explanation && (
                          <div style={{ fontSize: '0.76rem', color: 'var(--amber)', marginBottom: 2 }}>
                            ⚠ {tx.lost_explanation}
                            {tx.lost_wholly_business !== null && (
                              <span style={{ marginLeft: 5, color: 'var(--ink-faint)' }}>
                                · Wholly business: {tx.lost_wholly_business ? 'Yes' : 'No'}
                              </span>
                            )}
                          </div>
                        )}
                        {tx.client_note && (
                          <div style={{ fontSize: '0.76rem', color: 'var(--blue)', marginBottom: 2 }}>
                            💬 {tx.client_note}
                          </div>
                        )}
                        <BookkeeperNoteCell
                          transaction={tx}
                          isEditing={editingNoteTxId === tx.id}
                          onStartEdit={() => setEditingNoteTxId(tx.id)}
                          onStopEdit={() => setEditingNoteTxId(null)}
                          onSaved={load}
                        />
                        <AttachmentList transactionId={tx.id} onDeleted={load} />
                      </td>
                      <td>
                        <span style={{
                          fontSize: '0.73rem', fontWeight: 600, padding: '2px 7px', borderRadius: 100,
                          background: tx.amount > 0 ? 'var(--green-light)' : 'var(--surface-2)',
                          color: tx.amount > 0 ? 'var(--green)' : 'var(--ink-muted)',
                          whiteSpace: 'nowrap',
                        }}>
                          {tx.amount > 0 ? 'Income' : 'Expenditure'}
                        </span>
                      </td>
                      <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                        <AmountCell amount={tx.amount} />
                      </td>
                      <td>
                        <div style={{ display: 'inline-flex', gap: 2 }}>
                          <button
                            className="btn btn-ghost btn-sm btn-icon"
                            onClick={() => setEditingNoteTxId(tx.id)}
                            title={tx.bookkeeper_note ? 'Edit internal note' : 'Add internal note'}
                            style={{
                              color: tx.bookkeeper_note ? 'var(--green)' : 'var(--ink-faint)',
                              opacity: tx.bookkeeper_note ? 1 : 0.55,
                            }}
                          >📝</button>
                          {canDelete && (
                            <button
                              className="btn btn-ghost btn-sm btn-icon"
                              onClick={() => handleDeleteSingle(tx)}
                              title="Delete transaction"
                              style={{ color: '#dc2626' }}
                            >🗑</button>
                          )}
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      </main>

      {/* Add-transactions modal */}
      {showAddModal && (
        <AddTransactionsModal
          mode={addMode}
          setMode={setAddMode}
          manualRows={manualRows}
          onChangeManualRow={handleManualRowChange}
          onAddManualRow={handleAddManualRow}
          onRemoveManualRow={handleRemoveManualRow}
          csvParsedRows={csvParsedRows}
          csvParsing={csvParsing}
          onCsvFile={handleCsvFile}
          onClearCsv={() => setCsvParsedRows(null)}
          csvMappingRows={csvMappingRows}
          onCsvMappingApply={handleCsvMappingApply}
          onCsvMappingCancel={handleCsvMappingCancel}
          saving={addingTransactions}
          onSave={handleSaveNewTransactions}
          onClose={closeAddModal}
        />
      )}

      {/* Bulk description editor modal */}
      {showDescriptionEditor && (
        <BulkDescriptionEditor
          transactions={(request?.transactions || []).filter(t => selected.has(t.id))}
          drafts={descriptionDrafts}
          onChange={handleDescriptionDraftChange}
          saving={savingDescriptions}
          onSave={handleSaveDescriptions}
          onClose={closeDescriptionEditor}
        />
      )}
    </>
  )
}

// ─── ModalShell ───────────────────────────────────────────────────────────────
// Reusable shell for the two modals below: a centred panel on a dimmed
// overlay, with a header (title + close), a scrolling body, and a sticky
// footer for actions. Stops click-through on the panel itself so clicking
// inside it doesn't dismiss the modal.

function ModalShell({ title, onClose, footer, children, maxWidth = 720 }) {
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(20, 25, 40, 0.55)',
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        padding: '40px 16px', overflowY: 'auto',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: 'var(--white)', borderRadius: 12,
          width: '100%', maxWidth,
          maxHeight: 'calc(100vh - 80px)',
          display: 'flex', flexDirection: 'column',
          boxShadow: '0 24px 60px rgba(0,0,0,0.18)',
        }}
      >
        <div style={{
          padding: '18px 24px', borderBottom: '1px solid var(--surface-2)',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          flexShrink: 0,
        }}>
          <h2 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 600 }}>{title}</h2>
          <button
            onClick={onClose}
            className="btn btn-ghost btn-sm"
            style={{ minWidth: 0, padding: '4px 8px' }}
            aria-label="Close"
          >
            ✕
          </button>
        </div>
        <div style={{ padding: '20px 24px', overflowY: 'auto', flex: 1 }}>
          {children}
        </div>
        {footer && (
          <div style={{
            padding: '14px 24px', borderTop: '1px solid var(--surface-2)',
            display: 'flex', justifyContent: 'flex-end', gap: 10,
            flexShrink: 0,
            background: 'var(--surface)',
            borderRadius: '0 0 12px 12px',
          }}>
            {footer}
          </div>
        )}
      </div>
    </div>
  )
}

// ─── AddTransactionsModal ─────────────────────────────────────────────────────
// Two-tab modal for appending transactions to an existing request:
//   - Manual: a small grid of editable rows
//   - CSV: drop a file, preview parsed rows, confirm
// Both submit through the same save handler on the parent.

function AddTransactionsModal({
  mode, setMode,
  manualRows, onChangeManualRow, onAddManualRow, onRemoveManualRow,
  csvParsedRows, csvParsing, onCsvFile, onClearCsv,
  csvMappingRows, onCsvMappingApply, onCsvMappingCancel,
  saving, onSave, onClose,
}) {
  const tabBtnStyle = (active) => ({
    padding: '8px 14px',
    fontSize: '0.85rem',
    fontWeight: 500,
    background: active ? 'var(--accent-light)' : 'transparent',
    color: active ? 'var(--accent)' : 'var(--ink-muted)',
    border: 'none',
    borderRadius: 6,
    cursor: 'pointer',
  })

  return (
    <ModalShell
      title="Add transactions"
      onClose={onClose}
      maxWidth={760}
      footer={
        // Hide the modal's own footer when the custom mapper is showing,
        // since the mapper renders its own Back/Apply buttons. Two sets of
        // action buttons would be confusing.
        csvMappingRows ? null : (
          <>
            <button className="btn btn-ghost" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button className="btn btn-primary" onClick={onSave} disabled={saving}>
              {saving ? 'Adding…' : 'Add transactions'}
            </button>
          </>
        )
      }
    >
      {/* Mode switcher */}
      <div style={{
        display: 'inline-flex', gap: 4, padding: 4,
        background: 'var(--surface)', borderRadius: 8,
        marginBottom: 18,
      }}>
        <button style={tabBtnStyle(mode === 'manual')} onClick={() => setMode('manual')}>
          Type manually
        </button>
        <button style={tabBtnStyle(mode === 'csv')} onClick={() => setMode('csv')}>
          Upload CSV
        </button>
      </div>

      {mode === 'manual' && (
        <ManualEntryForm
          rows={manualRows}
          onChange={onChangeManualRow}
          onAdd={onAddManualRow}
          onRemove={onRemoveManualRow}
        />
      )}

      {mode === 'csv' && csvMappingRows && (
        <CsvColumnMapper
          rows={csvMappingRows}
          onApply={onCsvMappingApply}
          onCancel={onCsvMappingCancel}
        />
      )}

      {mode === 'csv' && !csvMappingRows && (
        <CsvUploadPane
          parsedRows={csvParsedRows}
          parsing={csvParsing}
          onFile={onCsvFile}
          onClear={onClearCsv}
        />
      )}
    </ModalShell>
  )
}

// Compact two-state toggle for marking a manually-entered transaction as
// either expenditure (default) or income. The amount input is always typed
// as a positive number; the sign is applied at save time based on this
// toggle. Visual style mirrors the Income/Expenditure pill in the main
// transaction table (green for income, neutral for expenditure).
function TypeToggle({ value, onChange }) {
  const isIncome = value === 'income'
  const baseBtn = {
    border: 'none',
    padding: '0 8px',
    height: 28,
    fontSize: '0.74rem',
    fontWeight: 600,
    cursor: 'pointer',
    borderRadius: 0,
    transition: 'background 0.12s, color 0.12s',
  }
  return (
    <div style={{
      display: 'inline-flex',
      border: '1px solid var(--surface-2)',
      borderRadius: 6,
      overflow: 'hidden',
      flexShrink: 0,
    }}
    role="group"
    aria-label="Transaction type"
    >
      <button
        type="button"
        onClick={() => onChange('expenditure')}
        aria-pressed={!isIncome}
        title="Expenditure (money out)"
        style={{
          ...baseBtn,
          background: !isIncome ? 'var(--surface-2)' : 'transparent',
          color:      !isIncome ? 'var(--ink)' : 'var(--ink-muted)',
        }}
      >
        Exp
      </button>
      <button
        type="button"
        onClick={() => onChange('income')}
        aria-pressed={isIncome}
        title="Income (money in)"
        style={{
          ...baseBtn,
          background: isIncome ? 'var(--green-light)' : 'transparent',
          color:      isIncome ? 'var(--green)' : 'var(--ink-muted)',
        }}
      >
        Inc
      </button>
    </div>
  )
}

function ManualEntryForm({ rows, onChange, onAdd, onRemove }) {
  return (
    <div>
      <p style={{ fontSize: '0.85rem', color: 'var(--ink-muted)', marginBottom: 14, lineHeight: 1.55 }}>
        Add one row per transaction. Empty rows are ignored when you save.
      </p>
      <div style={{ display: 'grid', gap: 8 }}>
        {/* Header row */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '120px 1fr 170px 1fr 36px',
          gap: 8, fontSize: '0.74rem', fontWeight: 600,
          textTransform: 'uppercase', letterSpacing: '0.04em',
          color: 'var(--ink-muted)', paddingBottom: 4,
        }}>
          <div>Date</div>
          <div>Description</div>
          <div>Amount (£)</div>
          <div>Payee (optional)</div>
          <div></div>
        </div>
        {rows.map((row, idx) => (
          <div key={idx} style={{
            display: 'grid',
            gridTemplateColumns: '120px 1fr 170px 1fr 36px',
            gap: 8, alignItems: 'center',
          }}>
            <input
              type="date"
              value={row.date}
              onChange={e => onChange(idx, 'date', e.target.value)}
              className="input"
              style={{ height: 36, fontSize: '0.85rem' }}
            />
            <input
              type="text"
              placeholder="e.g. Office supplies"
              value={row.description}
              onChange={e => onChange(idx, 'description', e.target.value)}
              className="input"
              style={{ height: 36, fontSize: '0.85rem' }}
            />
            {/* Amount input + income/expenditure toggle.
                User types a positive number; the toggle determines whether
                it gets stored as positive (income) or negative (expenditure)
                on save. Default is expenditure since that's what most
                receipt chasing is about. */}
            <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
              <input
                type="number"
                step="0.01"
                min="0"
                placeholder="0.00"
                value={row.amount}
                onChange={e => onChange(idx, 'amount', e.target.value)}
                className="input"
                style={{ height: 36, fontSize: '0.85rem', flex: 1, minWidth: 0 }}
              />
              <TypeToggle
                value={row.type || 'expenditure'}
                onChange={next => onChange(idx, 'type', next)}
              />
            </div>
            <input
              type="text"
              placeholder="Optional"
              value={row.payee}
              onChange={e => onChange(idx, 'payee', e.target.value)}
              className="input"
              style={{ height: 36, fontSize: '0.85rem' }}
            />
            <button
              onClick={() => onRemove(idx)}
              className="btn btn-ghost btn-sm"
              style={{ minWidth: 0, padding: '4px 8px', color: 'var(--ink-faint)' }}
              title="Remove this row"
              aria-label="Remove row"
            >
              ✕
            </button>
          </div>
        ))}
      </div>
      <button
        onClick={onAdd}
        className="btn btn-ghost btn-sm"
        style={{ marginTop: 12, color: 'var(--accent)' }}
      >
        + Add another row
      </button>
    </div>
  )
}

function CsvUploadPane({ parsedRows, parsing, onFile, onClear }) {
  if (parsing) {
    return (
      <div style={{
        padding: '40px 20px', textAlign: 'center',
        background: 'var(--surface)', borderRadius: 8,
        color: 'var(--ink-muted)',
      }}>
        Parsing CSV…
      </div>
    )
  }
  if (parsedRows && parsedRows.length > 0) {
    return (
      <div>
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          marginBottom: 12,
        }}>
          <span style={{ fontSize: '0.9rem', fontWeight: 500 }}>
            ✓ {parsedRows.length} transaction{parsedRows.length === 1 ? '' : 's'} parsed
          </span>
          <button className="btn btn-ghost btn-sm" onClick={onClear}>
            Upload a different file
          </button>
        </div>
        <div style={{
          maxHeight: 320, overflowY: 'auto',
          border: '1px solid var(--surface-2)', borderRadius: 8,
        }}>
          <table style={{ width: '100%', fontSize: '0.83rem' }}>
            <thead>
              <tr style={{ background: 'var(--surface)' }}>
                <th style={{ textAlign: 'left',  padding: '8px 10px', fontWeight: 600 }}>Date</th>
                <th style={{ textAlign: 'left',  padding: '8px 10px', fontWeight: 600 }}>Description</th>
                <th style={{ textAlign: 'right', padding: '8px 10px', fontWeight: 600 }}>Amount</th>
              </tr>
            </thead>
            <tbody>
              {parsedRows.slice(0, 100).map((r, i) => (
                <tr key={i} style={{ borderTop: '1px solid var(--surface-2)' }}>
                  <td style={{ padding: '6px 10px', color: 'var(--ink-muted)' }}>{r.date || ''}</td>
                  <td style={{ padding: '6px 10px' }}>{r.description || ''}</td>
                  <td style={{ padding: '6px 10px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                    {r.amount}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {parsedRows.length > 100 && (
            <div style={{
              padding: '8px 12px', fontSize: '0.78rem', color: 'var(--ink-faint)',
              background: 'var(--surface)', textAlign: 'center',
            }}>
              Showing first 100 of {parsedRows.length}. All rows will be added on save.
            </div>
          )}
        </div>
      </div>
    )
  }
  // Empty state: drop / pick a file
  return (
    <label style={{
      display: 'block', cursor: 'pointer',
      border: '2px dashed var(--surface-3)', borderRadius: 8,
      padding: '40px 20px', textAlign: 'center',
      color: 'var(--ink-muted)',
      transition: 'border-color 0.15s, background 0.15s',
    }}
    onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--accent)'; e.currentTarget.style.background = 'var(--accent-light)' }}
    onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--surface-3)'; e.currentTarget.style.background = 'transparent' }}
    >
      <div style={{ fontSize: '2rem', marginBottom: 8 }}>📄</div>
      <div style={{ fontSize: '0.95rem', color: 'var(--ink)', fontWeight: 500, marginBottom: 4 }}>
        Click to choose a CSV file
      </div>
      <div style={{ fontSize: '0.82rem', color: 'var(--ink-muted)' }}>
        Same format as when you created the request
      </div>
      <input
        type="file"
        accept=".csv"
        onChange={e => { const f = e.target.files?.[0]; if (f) onFile(f) }}
        style={{ display: 'none' }}
      />
    </label>
  )
}

// ─── BulkDescriptionEditor ────────────────────────────────────────────────────
// Shows the selected rows in a panel, each description editable. Save commits
// every changed row in one batch. Untouched rows aren't sent.

function BulkDescriptionEditor({ transactions, drafts, onChange, saving, onSave, onClose }) {
  return (
    <ModalShell
      title={`Edit descriptions (${transactions.length})`}
      onClose={onClose}
      maxWidth={680}
      footer={
        <>
          <button className="btn btn-ghost" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={onSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </>
      }
    >
      <p style={{ fontSize: '0.85rem', color: 'var(--ink-muted)', marginBottom: 16, lineHeight: 1.55 }}>
        Clarify the descriptions so your client recognises each transaction.
        Untouched rows are left as they are.
      </p>
      <div style={{ display: 'grid', gap: 12 }}>
        {transactions.map(tx => (
          <div key={tx.id} style={{
            border: '1px solid var(--surface-2)', borderRadius: 8,
            padding: '12px 14px',
          }}>
            <div style={{
              fontSize: '0.78rem', color: 'var(--ink-muted)', marginBottom: 8,
              display: 'flex', justifyContent: 'space-between', gap: 8,
              flexWrap: 'wrap',
            }}>
              <span>{tx.date ? new Date(tx.date).toLocaleDateString('en-GB') : 'No date'}</span>
              <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 500 }}>
                {typeof tx.amount === 'number' && !Number.isNaN(tx.amount)
                  ? `£${Math.abs(tx.amount).toFixed(2)}`
                  : ''}
              </span>
            </div>
            <input
              type="text"
              value={drafts[tx.id] ?? ''}
              onChange={e => onChange(tx.id, e.target.value)}
              className="input"
              style={{ fontSize: '0.9rem' }}
              autoFocus={transactions[0]?.id === tx.id}
            />
            {tx.description && (drafts[tx.id] ?? '') !== tx.description && (
              <div style={{
                fontSize: '0.76rem', color: 'var(--ink-faint)',
                marginTop: 6, fontStyle: 'italic',
              }}>
                was: {tx.description}
              </div>
            )}
          </div>
        ))}
      </div>
    </ModalShell>
  )
}
